import { supabase } from '../lib/supabase';

// Color palette for presence avatars
const PRESENCE_COLORS = [
  '#6366f1', '#8b5cf6', '#ec4899', '#f59e0b',
  '#10b981', '#3b82f6', '#ef4444', '#14b8a6',
  '#f97316', '#84cc16',
];

let colorIndex = 0;
const userColorMap = {};

// Module-level registry: channelName -> channel instance
// This ensures we can reliably find and remove channels across re-renders
const channelRegistry = new Map();

export function getPresenceColor(userId) {
  if (!userColorMap?.[userId]) {
    userColorMap[userId] = PRESENCE_COLORS?.[colorIndex % PRESENCE_COLORS?.length];
    colorIndex++;
  }
  return userColorMap?.[userId];
}

export function getInitials(profile) {
  if (!profile) return '?';
  if (profile?.nickname) return profile?.nickname?.slice(0, 2)?.toUpperCase();
  if (profile?.fullName) {
    const parts = profile?.fullName?.trim()?.split(' ');
    if (parts?.length >= 2) return (parts?.[0]?.[0] + parts?.[parts?.length - 1]?.[0])?.toUpperCase();
    return parts?.[0]?.slice(0, 2)?.toUpperCase();
  }
  if (profile?.email) return profile?.email?.slice(0, 2)?.toUpperCase();
  return '?';
}

/**
 * Join a presence channel for a specific project or view.
 * @param {string} channelKey - e.g. "project:123" or "gantt:123"
 * @param {object} userProfile - current user's profile
 * @param {function} onPresenceChange - callback(presenceList)
 * @returns {{ channel, leave }}
 */
export function joinPresenceChannel(channelKey, userProfile, onPresenceChange) {
  if (!channelKey || !userProfile) return { channel: null, leave: () => {} };

  const channelName = `presence:${channelKey}`;

  // If a channel with this name already exists in our registry, remove it first.
  // Using our own Map is reliable — no need to search supabase.getChannels()
  // which may have timing/naming inconsistencies.
  if (channelRegistry?.has(channelName)) {
    const oldChannel = channelRegistry?.get(channelName);
    try {
      supabase?.removeChannel(oldChannel);
    } catch (_) {
      // ignore errors during cleanup
    }
    channelRegistry?.delete(channelName);
  }

  const channel = supabase?.channel(channelName, {
    config: { presence: { key: userProfile?.id } },
  });

  // Store in registry immediately after creation, before subscribe
  channelRegistry?.set(channelName, channel);

  const syncPresence = () => {
    const state = channel?.presenceState();
    const list = Object.values(state)?.flat()?.filter(Boolean);
    onPresenceChange?.(list);
  };

  // Register ALL event listeners BEFORE calling subscribe()
  channel
    ?.on('presence', { event: 'sync' }, syncPresence)
    ?.on('presence', { event: 'join' }, syncPresence)
    ?.on('presence', { event: 'leave' }, syncPresence)
    ?.subscribe(async (status) => {
      if (status === 'SUBSCRIBED') {
        await channel?.track({
          userId: userProfile?.id,
          email: userProfile?.email || '',
          fullName: userProfile?.fullName || '',
          nickname: userProfile?.nickname || '',
          avatarUrl: userProfile?.avatarUrl || null,
          color: getPresenceColor(userProfile?.id),
          joinedAt: new Date()?.toISOString(),
        });
      }
    });

  const leave = async () => {
    try {
      await channel?.untrack();
      supabase?.removeChannel(channel);
    } catch (_) {
      // ignore errors during cleanup
    }
    channelRegistry?.delete(channelName);
  };

  return { channel, leave };
}
